import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function createIcon(path: ReactNode, viewBox = "0 0 24 24") {
  return function Icon({ className, ...props }: IconProps) {
    return (
      <svg
        aria-hidden="true"
        viewBox={viewBox}
        fill="none"
        className={className}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
      >
        {path}
      </svg>
    );
  };
}

function createFilledIcon(path: ReactNode, viewBox = "0 0 24 24") {
  return function Icon({ className, ...props }: IconProps) {
    return (
      <svg
        aria-hidden="true"
        viewBox={viewBox}
        fill="currentColor"
        className={className}
        {...props}
      >
        {path}
      </svg>
    );
  };
}

export const ArrowRightIcon = createIcon(<path d="M5 12h14m-5-5 5 5-5 5" />);
export const ArrowLeftIcon = createIcon(<path d="M19 12H5m5-5-5 5 5 5" />);
export const CalendarIcon = createIcon(
  <>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M8 3.5v4M16 3.5v4M3.5 9.5h17" />
  </>,
);
export const ClockIcon = createIcon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v4.8l3.2 1.9" />
  </>,
);
export const MaterialCalendarIcon = createFilledIcon(
  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />,
);
export const MaterialLocationOnIcon = createFilledIcon(
  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />,
);
export const MaterialArrowDropDownIcon = createFilledIcon(
  <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />,
);
export const MaterialGridViewIcon = createFilledIcon(
  <path d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z" />,
);
export const MaterialAccountCircleIcon = createFilledIcon(
  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />,
);
export const MaterialLogoutIcon = createFilledIcon(
  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />,
);
export const CheckIcon = createIcon(<path d="m5 12 4 4 10-10" />);
export const CloseIcon = createIcon(<path d="m6 6 12 12M18 6 6 18" />);
export const CopyIcon = createIcon(
  <>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
  </>,
);
export const DownloadIcon = createIcon(
  <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 20h14" />,
);
export const ExternalLinkIcon = createIcon(
  <>
    <path d="M14 5h5v5" />
    <path d="M10 14 19 5" />
    <path d="M19 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4" />
  </>,
);
export const EyeIcon = createIcon(
  <>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const EyeOffIcon = createIcon(
  <>
    <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <path d="m2 2 20 20" />
  </>,
);
export const FilterIcon = createIcon(<path d="M4 6h16M7 12h10M10 18h4" />);
export const MessageIcon = createIcon(
  <>
    <path d="M4 5h16v11H8l-4 4V5Z" />
  </>,
);
export const PencilIcon = createIcon(
  <>
    <path d="m4 20 4.5-1 9.5-9.5-3.5-3.5L5 15.5 4 20Z" />
    <path d="m13.5 6 3.5 3.5" />
  </>,
);
// Filled "edit" pencil matching the Material Icons `edit` glyph used in the
// Figma design. Rendered solid (fill) rather than the outline PencilIcon.
export function EditIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...props}
    >
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );
}
export const PinIcon = createIcon(
  <>
    <path d="M12 21s-5.5-5.9-5.5-10A5.5 5.5 0 0 1 12 5.5 5.5 5.5 0 0 1 17.5 11c0 4.1-5.5 10-5.5 10Z" />
    <circle cx="12" cy="11" r="2" />
  </>,
);
export const PlusIcon = createIcon(<path d="M12 5v14M5 12h14" />);
export const SearchIcon = createIcon(
  <path d="m21 21-4.3-4.3M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" />,
);
export const SendIcon = createIcon(
  <path d="m4 12 15-7-4 7 4 7-15-7Zm0 0h11" />,
);
export const SettingsIcon = createIcon(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V22a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H2a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V2a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1H22a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.5 1Z" />
  </>,
);
export const ShieldIcon = createIcon(
  <>
    <path d="M12 3 5 6v5c0 5 3.5 8 7 10 3.5-2 7-5 7-10V6l-7-3Z" />
    <path d="m9.5 12 2 2 3.5-4" />
  </>,
);
export const TrendingIcon = createIcon(
  <path d="M4 16 10 10l4 4 6-8M4 20h16" />,
);
export const UserCircleIcon = createIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="9.5" r="2.5" />
    <path d="M7.5 17c1-2 2.8-3 4.5-3s3.5 1 4.5 3" />
  </>,
);
export const UserGroupIcon = createIcon(
  <>
    <circle cx="9" cy="9" r="2.5" />
    <circle cx="16.5" cy="10.5" r="2" />
    <path d="M4.5 18c1.1-2.6 3-3.9 5.5-3.9s4.4 1.3 5.5 3.9" />
    <path d="M15 17.5c.6-1.7 1.8-2.6 3.5-2.6.8 0 1.4.2 2 .5" />
  </>,
);
